/**
 * 정산 수수료 업로드 검증 — 레거시 `commissionUploadValidation.js` 핵심 포팅
 */
import { pharmaService } from '@/entities/pharma/api/pharma.api';
import {
  isCsvFile,
  isSpreadsheetFile,
  readFileAsText,
  SPREADSHEET_UPLOAD_ACCEPT,
} from '@/shared/utils/csvImport';
import {
  COMMISSION_DRIVE_HEADERS,
  DRIVE_COL,
  findDriveHeaderRowIndex,
  isDriveFormatHeader,
} from '@/pages/settlement-fees/commissionDriveFormat';
import {
  normalizeCommissionDrugCode,
  normalizeCommissionDrugCodeToNineDigits,
} from '@/pages/settlement-fees/commissionDrugCode';
import * as XLSX from 'xlsx';

export const COMMISSION_UPLOAD_OUTPUT_HEADERS = [
  'reference_year_month',
  'drug_code',
  'drug_name',
  'pharma_company_id',
  'price',
  'standard_commission_rate',
  'standard_commission_note',
  'incentive',
  'incentive_note',
  'out_of_stock',
  'out_of_stock_from',
  'out_of_stock_to',
  'filtering_disabled_flag',
] as const;

/** 드라이브 양식을 서버 표준(영문 헤더)으로 변환한 다운로드 파일명 */
export const COMMISSION_PROCESSED_FILE_NAME = '정산수수료_변환파일.xlsx';

/** 정산수수료 업로드 — CSV·엑셀 input accept */
export const COMMISSION_UPLOAD_ACCEPT = SPREADSHEET_UPLOAD_ACCEPT;

export type CommissionUploadValidationResult =
  | { valid: true; processedFile?: File }
  | {
      valid: false;
      reason: string;
      message?: string;
      invalidCodes?: string[];
      invalidDrugCodes?: string[];
      unknownPharmaNames?: string[];
      unmatchedDrugCodes?: string[];
      expectedReferenceYearMonth?: string;
      mismatchDetails?: Array<{ drugCode: string; fileYearMonth: string }>;
    };

const normalizeHeaderLoose = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, '')
    .toLowerCase();

const DRUG_CODE_HEADERS = [
  'drug_code',
  'drugcode',
  '제품코드',
  '의약품코드',
  'edi코드',
];

const PHARMA_ID_HEADERS = ['pharma_company_id', 'pharmacompanyid'];

const toYearMonth = (value: unknown): string =>
  String(value ?? '').replace(/\D/g, '').slice(0, 6);

const locateHeaderAndData = (rows: unknown[][]) => {
  // 제약사 드라이브 내보내기는 1행에 '필수' 표시, 2행에 실제 헤더가 온다.
  const driveHeaderIdx = findDriveHeaderRowIndex(rows);
  if (driveHeaderIdx >= 0) {
    return {
      headerRow: (rows[driveHeaderIdx] ?? []) as unknown[],
      dataRows: rows.slice(driveHeaderIdx + 1) as unknown[][],
    };
  }
  return {
    headerRow: (rows[0] ?? []) as unknown[],
    dataRows: rows.slice(1) as unknown[][],
  };
};

const parseSpreadsheet = async (file: File) => {
  if (isCsvFile(file) && !isSpreadsheetFile(file)) {
    const text = await readFileAsText(file);
    const workbook = XLSX.read(text, { type: 'string', raw: false });
    const sheetName = workbook.SheetNames[0];
    const rows = XLSX.utils.sheet_to_json<unknown[]>(
      workbook.Sheets[sheetName],
      { header: 1, defval: null },
    );
    return locateHeaderAndData(rows);
  }

  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', raw: false });
  const sheetName = workbook.SheetNames[0];
  const rows = XLSX.utils.sheet_to_json<unknown[]>(
    workbook.Sheets[sheetName],
    { header: 1, defval: null },
  );
  return locateHeaderAndData(rows);
};

const findColumnIndex = (headerRow: unknown[], candidates: string[]): number => {
  const normalized = headerRow.map(normalizeHeaderLoose);
  for (const candidate of candidates) {
    const idx = normalized.indexOf(normalizeHeaderLoose(candidate));
    if (idx >= 0) return idx;
  }
  return -1;
};

const fetchPharmaLookup = async () => {
  const ids = new Set<string>();
  const nameToId = new Map<string, string>();
  let page = 0;
  const size = 200;

  for (;;) {
    const response = await pharmaService.getPharmaCompanies({}, { page, size });
    const content = response.content ?? [];
    content.forEach((company) => {
      const id = String(company.pharmaCompanyId).trim();
      const name = String(company.pharmaCompanyName).trim();
      if (id) ids.add(id);
      if (name) nameToId.set(normalizeHeaderLoose(name), id);
    });
    if (response.isLast || content.length < size) break;
    page += 1;
  }

  return { ids, nameToId };
};

const parseUploadPrice = (value: unknown): number | null => {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = Number.parseFloat(
    String(value).replace(/,/g, '').replace(/원/g, '').trim(),
  );
  return Number.isFinite(parsed) ? parsed : null;
};

/** 서버는 0~1 소수 수수료율을 받는다. 1 초과 값(65 등)만 100으로 나눈다. */
const parseUploadRate = (value: unknown): number | null => {
  const parsed = parseUploadPrice(value);
  if (parsed == null) return null;
  const normalized = parsed > 1 ? parsed / 100 : parsed;
  return Number(normalized.toFixed(4));
};

const toOptionalCell = (value: unknown): string | number | null => {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const text = String(value).trim();
  return text === '' ? null : text;
};

const toPharmaCompanyIdCell = (id: string): string | number =>
  /^\d+$/.test(id) ? Number(id) : id;

const rowsToProcessedXlsx = (rows: unknown[][], fileName: string): File => {
  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  const buffer = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
  return new File([buffer], fileName, {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};

/**
 * 드라이브 양식(년월/제약사/제품코드…)을 서버가 읽는 표준 열 순서로 변환한다.
 * 원본 1행의 '필수' 표시를 그대로 올리면 price 열이 문자라 서버가 거부한다.
 */
const buildDriveProcessedFile = (
  dataRows: unknown[][],
  nameToId: Map<string, string>,
  expectedReferenceYearMonth: string,
): File => {
  const outputRows: unknown[][] = [[...COMMISSION_UPLOAD_OUTPUT_HEADERS]];

  dataRows.forEach((row) => {
    const drugCode = normalizeCommissionDrugCode(row[DRIVE_COL.drugCode]);
    if (!drugCode) return;

    const pharmaName = String(row[DRIVE_COL.pharmaName] ?? '').trim();
    const pharmaCompanyId = nameToId.get(normalizeHeaderLoose(pharmaName));
    if (!pharmaCompanyId) return;

    outputRows.push([
      toYearMonth(row[DRIVE_COL.yearMonth]) || expectedReferenceYearMonth,
      drugCode,
      String(row[DRIVE_COL.drugName] ?? '').trim(),
      toPharmaCompanyIdCell(pharmaCompanyId),
      parseUploadPrice(row[DRIVE_COL.price]) ?? 0,
      parseUploadRate(row[DRIVE_COL.commissionRate]) ?? 0,
      toOptionalCell(row[DRIVE_COL.commissionNote]),
      toOptionalCell(row[DRIVE_COL.incentive]),
      toOptionalCell(row[DRIVE_COL.incentiveNote]),
      toOptionalCell(row[DRIVE_COL.outOfStock]),
      toOptionalCell(row[DRIVE_COL.outOfStockFrom]),
      toOptionalCell(row[DRIVE_COL.outOfStockTo]),
      toOptionalCell(row[DRIVE_COL.filteringDisabled]),
    ]);
  });

  return rowsToProcessedXlsx(outputRows, COMMISSION_PROCESSED_FILE_NAME);
};

const validateDriveFormat = async (
  headerRow: unknown[],
  dataRows: unknown[][],
  expectedReferenceYearMonth: string,
): Promise<CommissionUploadValidationResult> => {
  const invalidCodes: string[] = [];
  const unknownPharmaNames = new Set<string>();
  const unmatchedDrugCodes: string[] = [];
  const mismatchDetails: Array<{ drugCode: string; fileYearMonth: string }> =
    [];

  const { nameToId } = await fetchPharmaLookup();

  dataRows.forEach((row) => {
    const rawCode = row[DRIVE_COL.drugCode];
    const display = normalizeCommissionDrugCode(rawCode);
    if (!display) return;

    const normalized = normalizeCommissionDrugCodeToNineDigits(rawCode);
    if (!normalized.ok && !invalidCodes.includes(display)) {
      invalidCodes.push(display);
    }

    const fileYm = toYearMonth(row[DRIVE_COL.yearMonth]);
    if (fileYm && fileYm !== expectedReferenceYearMonth) {
      mismatchDetails.push({ drugCode: display, fileYearMonth: fileYm });
    }

    const pharmaName = String(row[DRIVE_COL.pharmaName] ?? '').trim();
    if (
      pharmaName &&
      !nameToId.has(normalizeHeaderLoose(pharmaName)) &&
      !unknownPharmaNames.has(pharmaName)
    ) {
      unknownPharmaNames.add(pharmaName);
      unmatchedDrugCodes.push(display);
    }
  });

  if (invalidCodes.length > 0) {
    return {
      valid: false,
      reason: 'invalid_drug_code_length',
      invalidCodes,
    };
  }

  if (mismatchDetails.length > 0) {
    return {
      valid: false,
      reason: 'year_month_mismatch',
      expectedReferenceYearMonth,
      mismatchDetails: mismatchDetails.slice(0, 20),
    };
  }

  if (unknownPharmaNames.size > 0) {
    return {
      valid: false,
      reason: 'unknown_pharma_company',
      unknownPharmaNames: [...unknownPharmaNames],
      unmatchedDrugCodes,
    };
  }

  if (!isDriveFormatHeader(headerRow)) {
    return { valid: false, reason: 'invalid_drive_header' };
  }

  return {
    valid: true,
    processedFile: buildDriveProcessedFile(
      dataRows,
      nameToId,
      expectedReferenceYearMonth,
    ),
  };
};

const validateStandardUploadFormat = async (
  headerRow: unknown[],
  dataRows: unknown[][],
): Promise<CommissionUploadValidationResult> => {
  const drugCodeColumnIndex = findColumnIndex(headerRow, DRUG_CODE_HEADERS);
  const pharmaCompanyIdColumnIndex = findColumnIndex(
    headerRow,
    PHARMA_ID_HEADERS,
  );

  if (drugCodeColumnIndex === -1) {
    return { valid: false, reason: 'missing_drug_code_column' };
  }
  if (pharmaCompanyIdColumnIndex === -1) {
    return { valid: false, reason: 'missing_pharma_company_id_column' };
  }

  const invalidCodes: string[] = [];
  dataRows.forEach((row) => {
    const rawCell = row[drugCodeColumnIndex];
    const display = String(rawCell ?? '').trim().replace(/\s+/g, '');
    if (!display) return;
    const result = normalizeCommissionDrugCodeToNineDigits(rawCell);
    if (!result.ok && !invalidCodes.includes(display)) {
      invalidCodes.push(display);
    }
  });

  if (invalidCodes.length > 0) {
    return {
      valid: false,
      reason: 'invalid_drug_code_length',
      invalidCodes,
    };
  }

  const { ids: allowedPharmaCompanyIds } = await fetchPharmaLookup();
  const invalidPharmaDrugCodes: string[] = [];

  dataRows.forEach((row) => {
    const drugCode = normalizeCommissionDrugCode(row[drugCodeColumnIndex]);
    if (!drugCode) return;
    const pharmaCompanyId = String(
      row[pharmaCompanyIdColumnIndex] ?? '',
    ).trim();
    if (!pharmaCompanyId || !allowedPharmaCompanyIds.has(pharmaCompanyId)) {
      if (!invalidPharmaDrugCodes.includes(drugCode)) {
        invalidPharmaDrugCodes.push(drugCode);
      }
    }
  });

  if (invalidPharmaDrugCodes.length > 0) {
    return {
      valid: false,
      reason: 'invalid_pharma_company_id',
      invalidDrugCodes: invalidPharmaDrugCodes,
    };
  }

  return { valid: true };
};

export const validateCommissionUploadFile = async (
  file: File,
  options: { expectedReferenceYearMonth?: string } = {},
): Promise<CommissionUploadValidationResult> => {
  const expectedYm = toYearMonth(options.expectedReferenceYearMonth ?? '');
  const { headerRow, dataRows } = await parseSpreadsheet(file);

  if (!headerRow.length) {
    return { valid: false, reason: 'missing_header' };
  }

  if (isDriveFormatHeader(headerRow)) {
    if (!expectedYm) {
      return { valid: false, reason: 'missing_expected_year_month' };
    }
    return validateDriveFormat(headerRow, dataRows, expectedYm);
  }

  const normalizedHeaders = headerRow.map(normalizeHeaderLoose);
  const hasDrivePartial = COMMISSION_DRIVE_HEADERS.slice(0, 8).every((header) =>
    normalizedHeaders.some(
      (cell) => cell === normalizeHeaderLoose(header),
    ),
  );
  if (hasDrivePartial) {
    if (!expectedYm) {
      return { valid: false, reason: 'missing_expected_year_month' };
    }
    return validateDriveFormat(headerRow, dataRows, expectedYm);
  }

  return validateStandardUploadFormat(headerRow, dataRows);
};
